import { useState, useEffect } from "react";
import { Header } from "@/components/Header";
import { LongDistanceLinks } from "@/components/seo/LongDistanceLinks";
import { Hero } from "@/components/Hero";
import { Footer } from "@/components/Footer";
import { EstimateForm } from "@/components/EstimateForm";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Shield } from "lucide-react";
import { SentryErrorBoundary } from "@/components/SentryErrorBoundary";
import { LazyOnVisible } from "@/components/performance/LazyOnVisible";
import { organizationSchema, localBusinessSchema, breadcrumbSchema, websiteSearchActionSchema, generateOrganizationSchemaWithReviews } from "@/lib/schemas";
import { useFeaturedReviews } from "@/hooks/useReviews";

// Skeleton placeholders for lazy-loaded components
const MapSkeleton = () => <div className="w-full h-[400px] bg-muted/50 rounded-lg animate-pulse flex items-center justify-center">
    <span className="text-muted-foreground">Loading map...</span>
  </div>;
const ReviewSkeleton = () => <section className="py-20 relative overflow-hidden">
    <div className="container mx-auto max-w-6xl">
      <div className="text-center mb-12">
        <div className="h-12 w-80 bg-muted/50 rounded mx-auto mb-4 animate-pulse" />
        <div className="h-8 w-60 bg-muted/50 rounded mx-auto animate-pulse" />
      </div>
      <div className="flex justify-center">
        <div className="w-full max-w-2xl h-[420px] bg-card/50 rounded-lg animate-pulse" />
      </div>
    </div>
  </section>;

const SectionSkeleton = ({ height = "400px" }: { height?: string }) => (
  <div className="w-full animate-pulse bg-muted/20" style={{ minHeight: height }} />
);

const Index = () => {
  const navigate = useNavigate();

  // Defer reviews query until page is idle (2s after mount)
  const [reviewsEnabled, setReviewsEnabled] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setReviewsEnabled(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const {
    data: featuredReviews = []
  } = useFeaturedReviews(reviewsEnabled);

  // Generate schemas - uses static schema initially, enhances with reviews when available
  const schemas = featuredReviews.length > 0 ? [generateOrganizationSchemaWithReviews(featuredReviews), localBusinessSchema, breadcrumbSchema, websiteSearchActionSchema] : [organizationSchema, localBusinessSchema, breadcrumbSchema, websiteSearchActionSchema];

  return <div className="min-h-screen">
      <SEOHead title="Chicago Movers & Storage | Home Moving Company | LifeTime Movers" description="Call (630) 686-9987 for an instant guaranteed quote. Chicago's 4.9★ top-rated home movers. Free 30-day storage on interstate moves, pad wrapping & $10K cargo protection." canonical="https://www.lifetimemovers.us" keywords="moving company near me, movers near me, home movers chicago, movers oak brook, naperville movers, hinsdale movers, long distance movers chicago, affordable moving quotes" schema={schemas} />
      <Header />
      <main id="main-content">
        <SentryErrorBoundary context="Hero" tags={{
        page: "homepage",
        component: "hero"
      }}>
          <Hero />
        </SentryErrorBoundary>

        {/* Instant estimate form — primary conversion point, on-page (no extra click) */}
        <section id="instant-estimate" className="py-12 bg-background scroll-mt-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-3">Get Your Instant Moving Quote</h2>
              <p className="text-lg text-muted-foreground">
                Guaranteed pricing in seconds — no phone call required. Licensed, insured, and trusted across Chicagoland.
              </p>
            </div>
            <SentryErrorBoundary context="HomepageEstimateForm" tags={{
              page: "homepage",
              component: "estimate-form"
            }}>
              <EstimateForm multiStep />
            </SentryErrorBoundary>
          </div>
        </section>

        {/* Popular long-distance destinations strip */}
        <section className="py-4 bg-background border-b border-border/40">
          <div className="container mx-auto px-4">
            <p className="text-center text-sm md:text-base text-muted-foreground">
              <strong className="text-foreground">Popular long-distance routes:</strong>{" "}
              <a href="/chicago-to-los-angeles-movers" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">Chicago to California</a>
              {" · "}
              <a href="/chicago-to-texas-movers" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">Chicago to Texas</a>
              {" · "}
              <a href="/chicago-to-florida-movers" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">Chicago to Florida</a>
              {" — plus "}
              <a href="/interstate-movers-chicago" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">20+ other states</a>. Get a guaranteed quote today.
            </p>
          </div>
        </section>

        {/* Service Authority Block - SEO Entity Statement (lightweight text, renders eagerly) */}

        <section className="py-12 bg-primary/5 border-y border-primary/10">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto text-center">
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                <strong className="text-foreground">LifeTime Movers</strong> (also known as <strong>Lifetime Moving Co.</strong>) is a licensed, full-service <strong>moving company in Chicago</strong> and one of the most trusted <strong>movers in Illinois</strong>, headquartered 
                in Oak Brook, IL. We handle{" "}
                <a href="/moving-services/local-move" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">local moves</a>,{" "}
                <a href="/long-distance-movers-chicago" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">long distance movers Chicago</a>,{" "}
                <a href="/interstate-movers-chicago" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">interstate movers Chicago</a>,{" "}
                <a href="/cross-country-movers-chicago" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">cross country movers Chicago</a>,{" "}
                <a href="/commercial-movers-near-me" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">commercial moves</a>, and{" "}
                <a href="/moving-services/office-move" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">office relocations</a> throughout 
                Chicagoland and beyond. Need to move on short notice? We've got you covered with flexible scheduling, even for last-minute requests.
              </p>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-6">
                As one of the top <a href="/chicago-suburb-movers" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">moving companies in the Chicago suburbs</a>, we serve 30+ communities including{" "}
                <a href="/service-areas/movers/naperville" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">Naperville movers</a>,{" "}
                <a href="/service-areas/movers/schaumburg" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">moving companies Schaumburg IL</a>,{" "}
                <a href="/oak-brook-movers-illinois" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">Oak Brook</a>,{" "}
                <a href="/locations/hinsdale-movers" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">Hinsdale</a>,{" "}
                <a href="/service-areas/movers/elmhurst" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">Elmhurst movers</a>,{" "}
                <a href="/service-areas/movers/bolingbrook" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">Bolingbrook moving company</a>,{" "}
                <a href="/service-areas/movers/downers-grove" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">Downers Grove movers</a>,{" "}
                <a href="/service-areas/movers/oak-park" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">Oak Park movers</a>,{" "}
                <a href="/service-areas/movers/oak-lawn" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">Oak Lawn movers</a>, and{" "}
                <a href="/service-areas/movers/dekalb" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">DeKalb movers</a>. Statewide, we're a top choice for <a href="/movers-in-illinois" className="text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary transition-colors">movers in Illinois</a>. 
                We also handle interstate moves to all 48 states, including popular routes like <strong>Chicago to Houston</strong> and <strong>Chicago to Texas</strong>. 
                Get your free <strong>moving quote</strong> in seconds with our online calculator.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-secondary" />
                  <span>USDOT 3733668 | MC 1318959</span>
                </span>
                <span className="hidden md:block">• </span>
                <span>Est. 2018 • 7+ Years Experience</span>
                <span className="hidden md:block">•</span>
                <a href="/service-areas/movers/chicago" className="text-primary hover:text-primary/80 font-medium underline decoration-primary/30 hover:decoration-primary transition-colors">
                  Chicago Movers Hub →
                </a>
                <span className="hidden md:block">•</span>
                <a href="/lifetime-movers-reviews" className="text-secondary hover:text-secondary/80 font-medium underline decoration-secondary/30 hover:decoration-secondary transition-colors">
                  4.9★ Rated by 5,000+ Customers →
                </a>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-6 mt-4 mx-auto">
                <a 
                  href="https://www.fmcsa.dot.gov/" 
                  target="_blank" 
                  rel="nofollow noopener noreferrer"
                  className="flex items-center justify-center border border-border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow bg-white min-w-[220px] min-h-[160px]"
                >
                  <img 
                    src="/images/fmcsa-logo.png" 
                    alt="FMCSA Registered Carrier" 
                    className="h-[140px] w-auto object-contain"
                    loading="lazy"
                  />
                </a>
                <a 
                  href="https://www.bbb.org/us/il/oak-brook/profile/moving-companies/lifetime-movers-0654-1000106466" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center border border-border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow bg-white min-w-[220px] min-h-[160px]"
                >
                  <img 
                    src="/images/bbb-accredited.png" 
                    alt="BBB Accredited Business" 
                    className="h-[140px] w-auto object-contain"
                    loading="lazy"
                  />
                </a>
                <a 
                  href="/lifetime-movers-reviews"
                  className="flex items-center justify-center border border-border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow bg-white min-w-[220px] min-h-[160px]"
                >
                  <img 
                    src="/images/lifetimemoversreviews.png" 
                    alt="LifeTime Movers Customer Reviews" 
                    className="h-[140px] w-auto object-contain"
                    loading="lazy"
                  />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Services - DEFERRED: 3 large images + heavy DOM */}
        <LazyOnVisible
          component={() => import("@/components/home/FeaturedServicesSection")}
          fallback={<SectionSkeleton height="600px" />}
          rootMargin="200px"
          minHeight="600px"
        />

        {/* Section 1: Why Chicago Families Choose LifeTime Movers - DEFERRED */}
        <LazyOnVisible
          component={() => import("@/components/home/WhyChicagoSection")}
          fallback={<SectionSkeleton height="400px" />}
          rootMargin="300px"
          minHeight="400px"
        />

        {/* Chicago Moving Services - Target head terms */}
        <LazyOnVisible
          component={() => import("@/components/home/ChicagoMovingServicesSection")}
          fallback={<SectionSkeleton height="400px" />}
          rootMargin="300px"
          minHeight="400px"
        />

        {/* Coverage Area Teaser */}
        <section className="py-16 bg-muted/30 content-visibility-auto">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">We Move You Anywhere</h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Pickup from Illinois, Indiana, Wisconsin & Michigan • Deliver to all 48 states
            </p>
            <div className="flex flex-wrap gap-4 justify-center mb-8">
              <div className="bg-background rounded-lg px-6 py-3 border-2 border-border">
                <span className="font-bold text-2xl text-secondary">4</span>
                <span className="text-muted-foreground ml-2">Pickup States</span>
              </div>
              <div className="bg-background rounded-lg px-6 py-3 border-2 border-border">
                <span className="font-bold text-2xl text-secondary">48</span>
                <span className="text-muted-foreground ml-2">Delivery States</span>
              </div>
            </div>
            
            {/* Animated US Service Map - Lazy Loaded Only When Near Viewport */}
            <div className="mb-12 py-8">
              <SentryErrorBoundary context="USServiceMap" tags={{
              page: "homepage",
              component: "service-map"
            }}>
                <LazyOnVisible
                  component={() => import("@/components/USServiceMap")}
                  fallback={<MapSkeleton />}
                  rootMargin="600px"
                  minHeight="400px"
                />
              </SentryErrorBoundary>
            </div>
            
            <Button variant="outline" size="xl" className="text-secondary border-secondary hover:bg-secondary hover:text-secondary-foreground" onClick={() => navigate('/service-areas')}>
              View Service Areas
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* Section 2: Why Thousands Request... - DEFERRED */}
        <LazyOnVisible
          component={() => import("@/components/home/WhyThousandsSection")}
          fallback={<SectionSkeleton height="400px" />}
          rootMargin="300px"
          minHeight="400px"
        />

        {/* Section 3: Chicago Suburbs - DEFERRED (11 cards, heavy DOM) */}
        <LazyOnVisible
          component={() => import("@/components/home/ChicagoSuburbsSection")}
          fallback={<SectionSkeleton height="500px" />}
          rootMargin="300px"
          minHeight="500px"
        />

        {/* Featured Moving Guides — gives blog posts inbound links from the
            homepage, our highest-authority surface. Critical for indexing. */}
        <LazyOnVisible
          component={() => import("@/components/home/FeaturedGuidesSection")}
          fallback={<SectionSkeleton height="500px" />}
          rootMargin="300px"
          minHeight="500px"
        />

        {/* Chicago Moving FAQs with FAQPage schema */}
        <LazyOnVisible
          component={() => import("@/components/home/ChicagoFAQSection")}
          fallback={<SectionSkeleton height="400px" />}
          rootMargin="300px"
          minHeight="400px"
        />

        {/* Former-mover successor mention — internal link into /former mesh */}
        <section className="container mx-auto px-4">
        </section>

        {/* Customer Reviews - Lazy Loaded Only When Near Viewport */}
        <section className="bg-muted/30">
          <SentryErrorBoundary context="ReviewCarousel" tags={{
          page: "homepage",
          component: "review-carousel"
        }}>
            <LazyOnVisible
              component={() => import("@/components/reviews/ReviewCarousel").then(m => ({ default: m.ReviewCarousel }))}
              fallback={<ReviewSkeleton />}
              rootMargin="600px"
              minHeight="420px"
            />
          </SentryErrorBoundary>
        </section>
        
        <LongDistanceLinks seed="home" />

        {/* CTA Section - DEFERRED (background image) */}
        <LazyOnVisible
          component={() => import("@/components/home/CTASection")}
          fallback={<SectionSkeleton height="300px" />}
          rootMargin="300px"
          minHeight="300px"
        />
      </main>
      <Footer />
    </div>;
};
export default Index;
